import * as React from 'react';
import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  TextField,
  Alert,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const MAPPING_TARGETS = [
  'source_code',
  'name',
  'brand',
  'purchase_price',
  'list_price',
  'group_code',
  'deposit',
  'currency',
  'ean',
  'search_code_1',
  'search_code_2',
  'stock_qty',
];

/**
 * Column-mapping editor for generic_csv / generic_xlsx catalogues.
 *
 * "Ehdota sarakkeet" previews the uploaded file and asks the AI for a
 * suggested mapping; the user confirms/corrects and saves. The mapping
 * persists on the catalogue so re-uploads are one click.
 */
function GenericMappingSection({ catalogue, onSaved, onError }) {
  const { t } = useTranslation();
  const config = catalogue.config || {};
  const [rows, setRows] = useState(null);
  const [mapping, setMapping] = useState(config.field_mapping || {});
  const [hasHeader, setHasHeader] = useState(config.has_header !== false);
  const [skipRows, setSkipRows] = useState(config.skip_rows || 0);
  const [suggesting, setSuggesting] = useState(false);
  const [saving, setSaving] = useState(false);

  const columnCount = rows && rows.length ? Math.max(...rows.map((r) => r.length)) : 0;
  const hasUpload = (catalogue.feeds || []).some((feed) => feed.role === 'combined');

  // target -> index inverted for the per-column selects
  const columnTargets = {};
  Object.entries(mapping).forEach(([target, idx]) => {
    columnTargets[idx] = target;
  });

  const handleSuggest = async () => {
    setSuggesting(true);
    try {
      const response = await axios.post(`/api/vendor-catalogues/${catalogue.id}/suggest-mapping/`, {});
      setRows(response.data.rows || []);
      const suggestedConfig = response.data.suggested_config || {};
      if (suggestedConfig.has_header !== undefined) setHasHeader(suggestedConfig.has_header);
      if (suggestedConfig.skip_rows !== undefined) setSkipRows(suggestedConfig.skip_rows);
      if (Object.keys(response.data.suggested_mapping || {}).length > 0) {
        setMapping(response.data.suggested_mapping);
      }
    } catch (err) {
      onError(err.response?.data?.detail || t('catalogues.mappingSuggestFailed'));
    } finally {
      setSuggesting(false);
    }
  };

  const handleColumnTargetChange = (columnIndex, target) => {
    setMapping((prev) => {
      const next = {};
      Object.entries(prev).forEach(([existingTarget, idx]) => {
        if (idx !== columnIndex && existingTarget !== target) {
          next[existingTarget] = idx;
        }
      });
      if (target) {
        next[target] = columnIndex;
      }
      return next;
    });
  };

  const mappingValid =
    mapping.source_code !== undefined &&
    (mapping.purchase_price !== undefined || mapping.list_price !== undefined);

  const handleSave = async () => {
    setSaving(true);
    try {
      const newConfig = {
        ...config,
        field_mapping: mapping,
        has_header: hasHeader,
        skip_rows: parseInt(skipRows, 10) || 0,
      };
      await axios.patch(`/api/vendor-catalogues/${catalogue.id}/`, { config: newConfig });
      onSaved();
    } catch {
      onError(t('catalogues.mappingSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const headerRow = hasHeader && rows && rows.length ? rows[0] : null;
  const sampleRows = rows ? (hasHeader ? rows.slice(1, 5) : rows.slice(0, 4)) : [];

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 3 }} data-testid="generic-mapping-section">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('catalogues.fieldMapping')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={suggesting ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
            onClick={handleSuggest}
            disabled={suggesting || !hasUpload}
            data-testid="suggest-mapping-button"
          >
            {t('catalogues.suggestMapping')}
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !mappingValid}
            data-testid="save-mapping-button"
          >
            {t('Save')}
          </Button>
        </Box>
      </Box>

      {!hasUpload && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('catalogues.uploadBeforeMapping')}
        </Alert>
      )}

      {Object.keys(mapping).length > 0 && !rows && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('catalogues.mappingConfigured')}
        </Alert>
      )}

      {!mappingValid && Object.keys(mapping).length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('catalogues.mappingIncomplete')}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <FormControlLabel
          control={<Checkbox checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} />}
          label={t('catalogues.hasHeader')}
        />
        <TextField
          label={t('catalogues.skipRows')}
          type="number"
          size="small"
          value={skipRows}
          onChange={(e) => setSkipRows(e.target.value)}
          sx={{ width: 130 }}
        />
      </Box>

      {rows && columnCount > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {Array.from({ length: columnCount }).map((_, idx) => (
                  <TableCell key={idx} sx={{ minWidth: 150, verticalAlign: 'top' }}>
                    <Typography sx={{ fontSize: '12px', color: 'text.disabled', mb: 0.5 }}>
                      {headerRow ? headerRow[idx] || `#${idx}` : `#${idx}`}
                    </Typography>
                    <Select
                      size="small"
                      fullWidth
                      displayEmpty
                      value={columnTargets[idx] || ''}
                      onChange={(e) => handleColumnTargetChange(idx, e.target.value)}
                      data-testid={`mapping-select-${idx}`}
                    >
                      <MenuItem value="">
                        <em>{t('catalogues.notMapped')}</em>
                      </MenuItem>
                      {MAPPING_TARGETS.map((target) => (
                        <MenuItem key={target} value={target}>
                          {t(`catalogues.field_${target}`)}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sampleRows.map((row, rowIdx) => (
                <TableRow key={rowIdx}>
                  {Array.from({ length: columnCount }).map((_, idx) => (
                    <TableCell key={idx} sx={{ fontSize: '12px', color: 'text.secondary', whiteSpace: 'nowrap', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {row[idx] || ''}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
}

export default GenericMappingSection;
